import React, { useState } from 'react'
import cn from 'classnames'
import { LockKeyhole, Mail } from 'lucide-react'
import api from 'api/api'
import UserIcon from 'components/users/UserIcon'
import useGetUser from 'refactor/hooks/api/useGetUser'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { cacheQuest, getCachedQuest, getCachedUser } from 'state/cache'
import { QUEST_STREAM_CRUMB_CHROME_PX } from 'lib/staticGeometryChrome'
import {
    directMessageCounterpartId,
    resolveDirectMessageConversationTarget,
} from 'lib/directMessages'
import type { Quest } from 'types/quests'
import styles from './thin-parent.module.scss'

interface DirectMessageBranchCrumbProps {
    quest: Quest
    onHoverChange?: (hovered: boolean) => void
    onRowClick?: (ev: React.MouseEvent) => void
}

// One step up a direct message's chain (resolveDirectMessageConversationTarget):
// the cached thread, or fetched.
export async function loadQuest(questId: string) {
    const cachedQuest = getCachedQuest(questId)
    if (cachedQuest?.id) return cachedQuest

    const fetchedQuest = await api.getQuestQuery(questId)
    if (!fetchedQuest?.id || fetchedQuest.id === '__PRIVATE__') return null
    return cacheQuest(fetchedQuest) || fetchedQuest
}

// The context strip of a branch inside a direct message, in place of the
// stream pill and parent crumb. The pair see who the conversation is with, and
// it opens the conversation at the message the branch grew from. Someone
// invited into the branch sees only a lock: nothing above the branch is theirs.
const DirectMessageBranchCrumb: React.FC<DirectMessageBranchCrumbProps> = ({
    quest,
    onHoverChange,
    onRowClick,
}) => {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel.panelId)
    const [opening, setOpening] = useState(false)

    const counterpartId = directMessageCounterpartId(quest, gon.currentUser?.id)
    const { user: fetchedCounterpart } = useGetUser(counterpartId || undefined, {
        enabled: !!counterpartId,
        refetchOnMount: false,
    })
    const counterpart = fetchedCounterpart || getCachedUser(counterpartId)
    const name = counterpart?.name || ''

    const openConversation = async (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (opening) return

        setOpening(true)
        try {
            const target = await resolveDirectMessageConversationTarget(quest, loadQuest)
            if (!target) return
            await openRight(
                target.answerId
                    ? { questId: target.questId, answerId: target.answerId }
                    : { questId: target.questId },
                { metricInputEvent: ev },
            )
        } finally {
            setOpening(false)
        }
    }

    return (
        <div
            className={cn(styles.thinParentComp, onRowClick && styles.rowNavigable)}
            style={{ height: QUEST_STREAM_CRUMB_CHROME_PX }}
            data-answer-breadcrumb="true"
            onMouseEnter={() => onHoverChange?.(true)}
            onMouseLeave={() => onHoverChange?.(false)}
            onClick={onRowClick}
        >
            {counterpartId ? (
                <button
                    type="button"
                    className={styles.parentCrumbButton}
                    onClick={openConversation}
                    aria-busy={opening || undefined}
                    title={name ? `Direct messages with ${name}` : 'Direct messages'}
                    data-testid="direct-message-branch-crumb"
                >
                    <Mail size={15} className={styles.directMessageCrumbIcon} aria-hidden />
                    <span className={styles.parentUserIconContainer}>
                        <UserIcon
                            user={counterpart || { id: counterpartId }}
                            size="small"
                            contextStyles={styles.parentUserIcon}
                        />
                    </span>
                    <span className={styles.parentCrumbText}>{name}</span>
                </button>
            ) : (
                <span
                    className={cn(styles.directMessageLock)}
                    aria-label="Private"
                    data-testid="direct-message-branch-lock"
                >
                    <LockKeyhole size={15} aria-hidden />
                </span>
            )}
        </div>
    )
}

export default DirectMessageBranchCrumb
